import { useState } from 'react'
import {
  activateAndCreateLicense,
  passLicenseGate,
  passPaymentGate,
} from '../../services/licensing'
import type { Entitlement } from '../../types'
import type { ImportMode } from './ImportXeroSection'

interface ActivationStateInput {
  entitlements: Entitlement[]
  onRefresh: () => Promise<void>
}

export interface ActivationGateActions {
  passPayment: () => Promise<void>
  passLicense: () => Promise<void>
  activate: () => Promise<void>
}

export interface ActivationState {
  selectedEntitlementId: string | null
  setSelectedEntitlementId: (id: string) => void
  showImportSection: boolean
  toggleImportSection: () => void
  importMode: ImportMode
  setImportMode: (mode: ImportMode) => void
  isProcessing: boolean
  error: string | null
  setError: (error: string | null) => void
  dismissError: () => void
  gateActions: ActivationGateActions
  finishImport: () => Promise<void>
}

function initialSelected(entitlements: Entitlement[]): string | null {
  if (entitlements.length === 0) return null
  return entitlements[0]?.id ?? null
}

export function useActivationState(input: ActivationStateInput): ActivationState {
  const { entitlements, onRefresh } = input
  const [selectedEntitlementId, setSelectedEntitlementId] = useState<string | null>(
    initialSelected(entitlements)
  )
  const [showImportSection, setShowImportSection] = useState(true)
  const [importMode, setImportMode] = useState<ImportMode>('none')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runGate = async (
    action: (id: string) => Promise<unknown>,
    failureMessage: string
  ): Promise<void> => {
    if (!selectedEntitlementId) return
    setIsProcessing(true)
    setError(null)
    try {
      await action(selectedEntitlementId)
      await onRefresh()
    } catch (gateError) {
      setError(gateError instanceof Error ? gateError.message : failureMessage)
    } finally {
      setIsProcessing(false)
    }
  }

  const gateActions: ActivationGateActions = {
    passPayment: () => runGate(passPaymentGate, 'Failed to pass payment gate'),
    passLicense: () => runGate(passLicenseGate, 'Failed to pass license gate'),
    activate: () => runGate(activateAndCreateLicense, 'Failed to activate entitlement'),
  }

  const finishImport = async (): Promise<void> => {
    setImportMode('none')
    await onRefresh()
  }

  return {
    selectedEntitlementId,
    setSelectedEntitlementId,
    showImportSection,
    toggleImportSection: () => {
      setShowImportSection((prev) => !prev)
    },
    importMode,
    setImportMode,
    isProcessing,
    error,
    setError,
    dismissError: () => {
      setError(null)
    },
    gateActions,
    finishImport,
  }
}
